import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBudget } from '@/contexts/BudgetContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, Check, X, Edit2, Building2, Lock, Zap } from 'lucide-react';
import { ScanPackModal } from '@/components/subscription/ScanPackModal';

const CATEGORIES = [
  'Food & Dining', 'Transportation', 'Utilities', 'Entertainment', 
  'Shopping', 'Healthcare', 'Education', 'Savings', 'Salary', 
  'Freelance', 'Investment', 'Gift', 'Refund', 'Other'
];

export const ImportModals = () => {
  const { authFetch } = useAuth();
  const { reloadData } = useBudget();
  const subscription = useSubscription();
  
  // Scan pack modal
  const [showScanPackModal, setShowScanPackModal] = useState(false);
  
  // Bank Statement Import State
  const [showBankImportDialog, setShowBankImportDialog] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankPreview, setBankPreview] = useState(null);
  const [bankImporting, setBankImporting] = useState(false);
  const [bankEditingIdx, setBankEditingIdx] = useState(null);
  
  // PDF Import State
  const [showPdfImportDialog, setShowPdfImportDialog] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfEditingIdx, setPdfEditingIdx] = useState(null);
  
  // Projects for import destination
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Check if user can use AI features
  const canUseAI = subscription.canScan();

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await authFetch('/api/projects');
        if (response.ok) {
          const data = await response.json();
          setProjects(data);
        }
      } catch (error) {
        console.error('Failed to fetch projects');
      }
    };
    fetchProjects();
  }, [authFetch]);

  // Listen for header import events
  useEffect(() => {
    const handleBankImport = () => { 
      setBankPreview(null); 
      setSelectedProject(null); 
      setShowBankImportDialog(true); 
    };
    const handlePdfImport = () => { 
      setPdfPreview(null); 
      setSelectedProject(null); 
      setShowPdfImportDialog(true); 
    };
    
    window.addEventListener('openBankImport', handleBankImport);
    window.addEventListener('openPdfImport', handlePdfImport);
    
    return () => {
      window.removeEventListener('openBankImport', handleBankImport);
      window.removeEventListener('openPdfImport', handlePdfImport);
    };
  }, []);

  // Bank statement handlers
  const handleBankStatementUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check scan limit before processing
    if (!canUseAI) {
      toast.error('AI scan limit reached. Purchase more scans to continue.');
      setShowScanPackModal(true);
      return;
    }
    
    setBankLoading(true);
    setBankPreview(null);
    try {
      // Use a scan for AI processing
      await subscription.useScan();
      
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch('/api/import/bank-statement/preview', { method: 'POST', body: formData, headers: {} });
      const result = await response.json();
      if (response.ok) setBankPreview(result);
      else toast.error(result.detail || 'Failed to parse bank statement');
    } catch (error) {
      toast.error('Failed to upload bank statement');
    } finally {
      setBankLoading(false);
    }
  };

  const handleBankCategoryChange = (index, newCategory) => {
    if (!bankPreview) return;
    const updated = { ...bankPreview };
    updated.transactions[index].category = newCategory;
    setBankPreview(updated);
    setBankEditingIdx(null);
  };

  const handleConfirmBankImport = async () => {
    if (!bankPreview) return;
    setBankImporting(true);
    try {
      const response = await authFetch('/api/import/bank-statement/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactions: bankPreview.transactions, project_id: selectedProject })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Imported ${result.imported} transactions!`);
        await reloadData();
        setShowBankImportDialog(false);
        setBankPreview(null);
      } else toast.error(result.detail || 'Import failed');
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setBankImporting(false);
    }
  };

  // PDF Import handlers
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check scan limit before processing
    if (!canUseAI) {
      toast.error('AI scan limit reached. Purchase more scans to continue.');
      setShowScanPackModal(true);
      return;
    }
    
    setPdfLoading(true);
    setPdfPreview(null);
    try {
      // Use a scan for AI processing
      await subscription.useScan();
      
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch('/api/import/pdf/preview', { method: 'POST', body: formData, headers: {} });
      const result = await response.json();
      if (response.ok) setPdfPreview(result);
      else toast.error(result.detail || 'Failed to parse PDF');
    } catch (error) {
      toast.error('Failed to upload PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfCategoryChange = (index, newCategory) => {
    if (!pdfPreview) return;
    const updated = { ...pdfPreview };
    updated.transactions[index].category = newCategory;
    setPdfPreview(updated);
    setPdfEditingIdx(null);
  };

  const handleConfirmPdfImport = async () => {
    if (!pdfPreview) return;
    setPdfImporting(true);
    try {
      const response = await authFetch('/api/import/pdf/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactions: pdfPreview.transactions, project_id: selectedProject })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Imported ${result.imported} transactions!`);
        await reloadData();
        setShowPdfImportDialog(false);
        setPdfPreview(null);
      } else toast.error(result.detail || 'Import failed');
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setPdfImporting(false);
    }
  };

  // Build project options
  const projectOptions = [];
  projectOptions.push({ value: 'none', label: 'No Project (General)' });
  for (let i = 0; i < projects.length; i++) {
    projectOptions.push({ value: projects[i].id, label: projects[i].name });
  }

  // Build transaction rows for bank import
  const bankTransactionRows = [];
  if (bankPreview?.transactions) {
    for (let i = 0; i < bankPreview.transactions.length; i++) {
      const tx = bankPreview.transactions[i];
      bankTransactionRows.push(
        <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-sm font-medium truncate">{tx.description}</p>
            <p className="text-xs text-muted-foreground">{tx.date}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${tx.type === 'income' ? 'text-green-500' : 'text-red-500'}`}>
              {tx.type === 'income' ? '+' : '-'}${tx.amount.toFixed(2)}
            </span>
            {bankEditingIdx === i ? (
              <Select value={tx.category} onValueChange={(val) => handleBankCategoryChange(i, val)}>
                <SelectTrigger className="w-32 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setBankEditingIdx(i)}>
                {tx.category} <Edit2 className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      );
    }
  }

  // Build transaction rows for PDF import
  const pdfTransactionRows = [];
  if (pdfPreview?.transactions) {
    for (let i = 0; i < pdfPreview.transactions.length; i++) {
      const tx = pdfPreview.transactions[i];
      pdfTransactionRows.push(
        <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-sm font-medium truncate">{tx.description}</p>
            <p className="text-xs text-muted-foreground">{tx.date}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${tx.type === 'income' ? 'text-green-500' : 'text-red-500'}`}>
              {tx.type === 'income' ? '+' : '-'}${tx.amount.toFixed(2)}
            </span>
            {pdfEditingIdx === i ? (
              <Select value={tx.category} onValueChange={(val) => handlePdfCategoryChange(i, val)}>
                <SelectTrigger className="w-32 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setPdfEditingIdx(i)}>
                {tx.category} <Edit2 className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <>
      {/* Bank Statement Import Dialog */}
      <Dialog open={showBankImportDialog} onOpenChange={setShowBankImportDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Import Bank Statement
            </DialogTitle>
            <DialogDescription>
              Upload a CSV or Excel bank statement. AI will auto-categorize transactions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!bankPreview ? (
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleBankStatementUpload}
                  className="hidden"
                  id="bank-upload"
                  disabled={bankLoading}
                />
                <Label htmlFor="bank-upload" className="cursor-pointer">
                  {bankLoading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Analyzing statement...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <span className="text-sm font-medium">Click to upload CSV or Excel</span>
                      <span className="text-xs text-muted-foreground">Supports most bank formats</span>
                    </div>
                  )}
                </Label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-600">
                    Detected: {bankPreview.bank_detected}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Found {bankPreview.total_transactions} transactions
                    {bankPreview.date_range?.start && ` from ${bankPreview.date_range.start} to ${bankPreview.date_range.end}`}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Import to Project (Optional)</Label>
                  <Select value={selectedProject || 'none'} onValueChange={(val) => setSelectedProject(val === 'none' ? null : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="border rounded-lg p-3 max-h-60 overflow-y-auto">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Preview (click category to edit)</p>
                  {bankTransactionRows}
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setBankPreview(null)}>
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                  <Button className="flex-1 bg-green-500 hover:bg-green-600" onClick={handleConfirmBankImport} disabled={bankImporting}>
                    {bankImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Import {bankPreview.total_transactions} Transactions
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Import Dialog */}
      <Dialog open={showPdfImportDialog} onOpenChange={setShowPdfImportDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Import PDF Statement
            </DialogTitle>
            <DialogDescription>
              Upload a PDF bank statement or financial document. AI will extract and categorize transactions.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!pdfPreview ? (
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                  id="pdf-upload"
                  disabled={pdfLoading}
                />
                <Label htmlFor="pdf-upload" className="cursor-pointer">
                  {pdfLoading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Analyzing PDF with AI...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <span className="text-sm font-medium">Click to upload PDF</span>
                      <span className="text-xs text-muted-foreground">Text-based PDFs work best</span>
                    </div>
                  )}
                </Label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-600">
                    Extracted from: {pdfPreview.filename}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Found {pdfPreview.total_transactions} transactions
                    {pdfPreview.date_range?.start && ` from ${pdfPreview.date_range.start} to ${pdfPreview.date_range.end}`}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm">Import to Project (Optional)</Label>
                  <Select value={selectedProject || 'none'} onValueChange={(val) => setSelectedProject(val === 'none' ? null : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="border rounded-lg p-3 max-h-60 overflow-y-auto">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Preview (click category to edit)</p>
                  {pdfTransactionRows}
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setPdfPreview(null)}>
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                  <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={handleConfirmPdfImport} disabled={pdfImporting}>
                    {pdfImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Import {pdfPreview.total_transactions} Transactions
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Scan Pack Purchase Modal */}
      <ScanPackModal 
        open={showScanPackModal} 
        onOpenChange={setShowScanPackModal} 
      />
    </>
  );
};
